import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HotelProvider } from "./contexts/HotelContext";
import { ToastProvider } from "./contexts/ToastContext";
import { TurnstileProvider, getTurnstileSiteKey } from "./hooks/useTurnstile";
import Dashboard from "./pages/Dashboard";
import OfferList from "./pages/OfferList";
import CreateOffer from "./pages/CreateOffer";
import OfferDetails from "./pages/OfferDetails";
import OfferAnalytics from "./pages/OfferAnalytics";
import PublicOffer from "./pages/PublicOffer";
import Login from "./pages/Login";
import LoginSuccess from "./pages/LoginSuccess";
import LoginRedirect from "./pages/LoginRedirect";
import "./App.css";
import CategoryOptionsManagement from "./pages/CategoryOptionsManagement";
import AccountSettings from "./pages/AccountSettings";
import AccountSubscription from "./pages/AccountSubscription";
import HotelSettings from "./pages/HotelSettings";
import StatusPage from "./pages/StatusPage";
import ProtectedRoute from "./components/ProtectedRoute";
import ToastContainer from "./components/ToastContainer";
import BrandKitDesign from "./pages/BrandKitDesign";
import FilesManager from "./pages/FilesManager";
import MagicLinkVerify from "./pages/MagicLinkVerify";
import PasswordResetRequest from "./pages/PasswordResetRequest";
import PasswordReset from "./pages/PasswordReset";
import VerifyEmail from "./pages/VerifyEmail";
import GrantsArticles from "./pages/GrantsArticles";
import DiscoveryArticles from "./pages/DiscoveryArticles";
import GrantsArticlesTab from "./pages/GrantsArticlesTab";
import GrantsKfs from "./pages/GrantsKfs";
import BurArticles from "./pages/BurArticles";
import BurGrants from "./pages/BurGrants";
import GrantsCalls from "./pages/GrantsCalls";
import GrantsFormsCsvImport from "./pages/GrantsFormsCsvImport";
import GrantsFormsInteractive from "./pages/GrantsFormsInteractive";
import GrantsCallsArchive from "./pages/GrantsCallsArchive";
import GrantsCallDetails from "./pages/GrantsCallDetails";
import GrantsSources from "./pages/GrantsSources";
import GrantsPrograms from "./pages/GrantsPrograms";
import GrantsProgramDetails from "./pages/GrantsProgramDetails";
import GrantsOperators from "./pages/GrantsOperators";
import GrantsOperatorDetails from "./pages/GrantsOperatorDetails";
import GrantsCompetitions from "./pages/GrantsCompetitions";
import GrantsCompetitionDetails from "./pages/GrantsCompetitionDetails";
import GrantsProjects from "./pages/GrantsProjects";
import GrantsProjectDetails from "./pages/GrantsProjectDetails";
import GrantsProcessingConfigs from "./pages/GrantsProcessingConfigs";
import GrantsAdminRecommendations from "./pages/GrantsAdminRecommendations";
import GrantsRecommendationDetails from "./pages/GrantsRecommendationDetails";
import GrantsAlerts from "./pages/GrantsAlerts";
import TenantManagement from "./pages/TenantManagement";
import TenantDetails from "./pages/TenantDetails";
import TenantUsers from "./pages/TenantUsers";
import ApiKeys from "./pages/ApiKeys";
import Unsubscribed from "./pages/Unsubscribed";
import UnsubscribeRedirect from "./components/UnsubscribeRedirect";
import AdminRoute from "./components/AdminRoute";
import ModeratorRoute from "./components/ModeratorRoute";
import Help from "./pages/Help";
import { RegisterRoute } from "./pages/RegisterRoute";
import { RegistrationSuccess } from "./pages/RegistrationSuccess";
import { HotelB2BLegalPage } from "./pages/legal/HotelB2BLegalPage";
import SubscriptionPlansManagement from "./pages/admin/SubscriptionPlansManagement";
import BrandManagement from "./pages/admin/BrandManagement";
import KiedyNaborySettlements from "./pages/admin/KiedyNaborySettlements";
import AdminProformaInvoices from "./pages/admin/AdminProformaInvoices";
import LlmUsage from "./pages/admin/LlmUsage";
import FormGenerations from "./pages/admin/FormGenerations";
import EmailTracking from "./pages/admin/EmailTracking";
import CallViews from "./pages/admin/CallViews";
import OutboxDashboard from "./pages/admin/OutboxDashboard";
import WatermarkRecovery from "./pages/admin/WatermarkRecovery";
import AdminTenants from "./pages/admin/AdminTenants";
import AdminTenantDeletions from "./pages/admin/AdminTenantDeletions";
import AdminTenantDeletionDetails from "./pages/admin/AdminTenantDeletionDetails";
import BillingProducts from "./pages/admin/billing/BillingProducts";
import BillingCatalog from "./pages/admin/billing/BillingCatalog";
import BillingSubscriptions from "./pages/admin/billing/BillingSubscriptions";
import AdminTrialInvites from "./pages/admin/AdminTrialInvites";
import BillingOrders from "./pages/admin/billing/BillingOrders";
import BillingPricing from "./pages/admin/billing/BillingPricing";
import UnmatchedPaymentsPage from "./pages/admin/billing/UnmatchedPaymentsPage";
import ApiDocumentation from "./pages/ApiDocumentation";
import BillingDocuments from "./pages/BillingDocuments";
import TenantCompany from "./pages/TenantCompany";
import TenantOrders from "./pages/TenantOrders";
import ServiceAccounts from "./pages/ServiceAccounts";
import TrackingRedirect from "./pages/TrackingRedirect";
import GrantsProcessingErrors from "./pages/GrantsProcessingErrors";
import { GTMPageTracker } from "./components/GTMPageTracker";
import CookieBanner from "./components/CookieBanner";
import ErrorBoundary from "./components/common/ErrorBoundary";
import {
  LegalConsentProvider,
  LegalConsentRouteWatcher,
} from "./components/legal/LegalConsentProvider";

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
    },
  },
});

function App() {
  const turnstileSiteKey = getTurnstileSiteKey();

  return (
    <QueryClientProvider client={queryClient}>
      <TurnstileProvider siteKey={turnstileSiteKey}>
        <HotelProvider>
          <ToastProvider>
            {/* Second line of defense (code review C2): a fail-closed content
                lookup inside ReacceptanceModal is caught at the source (see
                ReacceptanceModal.tsx), but this boundary still guards against
                any other uncaught throw inside LegalConsentProvider's render
                (which sits above <Router>, so an escaping error would
                otherwise white-screen the whole app). */}
            <ErrorBoundary>
              <LegalConsentProvider>
                <Router>
                  <GTMPageTracker />
                <LegalConsentRouteWatcher />
                <div className="App">
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/login-success" element={<LoginSuccess />} />
                  <Route path="/login-redirect" element={<LoginRedirect />} />
                  <Route path="/auth/verify" element={<MagicLinkVerify />} />
                  <Route
                    path="/auth/forgot-password"
                    element={<PasswordResetRequest />}
                  />
                  <Route
                    path="/auth/reset-password"
                    element={<PasswordReset />}
                  />
                  <Route
                    path="/auth/verify-email"
                    element={<VerifyEmail />}
                  />
                  <Route path="/register" element={<RegisterRoute />} />
                  <Route
                    path="/registration/success"
                    element={<RegistrationSuccess />}
                  />
                  <Route path="/status" element={<StatusPage />} />
                  {/* KiedyNabory policy routes: hosted at kiedynabory.pl/polityka-prywatnosci
                      and kiedynabory.pl/regulamin (Astro landing, ADR-088 AC E).
                      HotelB2B has no landing app of its own, so its canonical legal
                      route lives here instead (D4) — PUBLIC, no <ProtectedRoute>. */}
                  <Route
                    path="/hotelb2b-regulamin"
                    element={<HotelB2BLegalPage documentType="terms" />}
                  />
                  <Route
                    path="/hotelb2b-polityka-prywatnosci"
                    element={<HotelB2BLegalPage documentType="privacy" />}
                  />
                  <Route path="/unsubscribed" element={<Unsubscribed />} />
                  <Route
                    path="/docs/api"
                    element={
                      <ProtectedRoute>
                        <ApiDocumentation />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/notifications/unsubscribe/:token"
                    element={<UnsubscribeRedirect />}
                  />
                  <Route
                    path="/grants/t/:token"
                    element={<TrackingRedirect />}
                  />
                  <Route path="/offers/public/:token" element={<PublicOffer />} />
                  <Route
                    path="/offers/public/:token/v/:versionToken"
                    element={<PublicOffer />}
                  />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/offers"
                    element={
                      <ProtectedRoute>
                        <OfferList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/offers/create"
                    element={
                      <ProtectedRoute>
                        <CreateOffer />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/offers/:id/edit"
                    element={
                      <ProtectedRoute>
                        <CreateOffer />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/offers/:offerId/analytics"
                    element={
                      <ProtectedRoute>
                        <OfferAnalytics />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/offers/:id"
                    element={
                      <ProtectedRoute>
                        <OfferDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/hotel/category-options"
                    element={
                      <ProtectedRoute>
                        <CategoryOptionsManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/account/settings"
                    element={
                      <ProtectedRoute>
                        <AccountSettings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tenant/subscription"
                    element={
                      <ProtectedRoute>
                        <AccountSubscription />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tenant/billing"
                    element={
                      <ProtectedRoute>
                        <BillingDocuments />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tenant/company"
                    element={
                      <ProtectedRoute>
                        <TenantCompany />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tenant/orders"
                    element={
                      <ProtectedRoute>
                        <TenantOrders />
                      </ProtectedRoute>
                    }
                  />
                  {/* Backward compatibility redirect */}
                  <Route
                    path="/account/subscription"
                    element={<Navigate to="/tenant/subscription" replace />}
                  />
                  <Route
                    path="/hotel/settings"
                    element={
                      <ProtectedRoute>
                        <HotelSettings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/hotel/design"
                    element={
                      <ProtectedRoute>
                        <BrandKitDesign />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/hotel/files"
                    element={
                      <ProtectedRoute>
                        <FilesManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/kfs/articles"
                    element={
                      <ProtectedRoute>
                        <GrantsArticles />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/kfs/grants"
                    element={
                      <ProtectedRoute>
                        <GrantsKfs />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/bur/articles"
                    element={
                      <ProtectedRoute>
                        <BurArticles />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/bur/grants"
                    element={
                      <ProtectedRoute>
                        <BurGrants />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/forms"
                    element={
                      <ProtectedRoute>
                        <GrantsFormsInteractive />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/forms/csv-import"
                    element={
                      <ProtectedRoute>
                        <GrantsFormsCsvImport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/calls"
                    element={
                      <ProtectedRoute>
                        <GrantsCalls />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/calls/archive"
                    element={
                      <ProtectedRoute>
                        <GrantsCallsArchive />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/calls/:id"
                    element={
                      <ProtectedRoute>
                        <GrantsCallDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/sources"
                    element={
                      <ProtectedRoute>
                        <ModeratorRoute>
                          <GrantsSources />
                        </ModeratorRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/sources/articles"
                    element={
                      <ProtectedRoute>
                        <ModeratorRoute>
                          <DiscoveryArticles />
                        </ModeratorRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/programs"
                    element={
                      <ProtectedRoute>
                        <GrantsPrograms />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/programs/:id"
                    element={
                      <ProtectedRoute>
                        <GrantsProgramDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/operators"
                    element={
                      <ProtectedRoute>
                        <GrantsOperators />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/operators/:id"
                    element={
                      <ProtectedRoute>
                        <GrantsOperatorDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/competitions"
                    element={
                      <ProtectedRoute>
                        <GrantsCompetitions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/competitions/:id"
                    element={
                      <ProtectedRoute>
                        <GrantsCompetitionDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/projects"
                    element={
                      <ProtectedRoute>
                        <GrantsProjects />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/projects/:id"
                    element={
                      <ProtectedRoute>
                        <GrantsProjectDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/admin/processing-configs"
                    element={
                      <ProtectedRoute>
                        <ModeratorRoute>
                          <GrantsProcessingConfigs />
                        </ModeratorRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/admin/processing-errors"
                    element={
                      <ProtectedRoute>
                        <ModeratorRoute>
                          <GrantsProcessingErrors />
                        </ModeratorRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/admin/recommendations"
                    element={
                      <ProtectedRoute>
                        <ModeratorRoute>
                          <GrantsAdminRecommendations />
                        </ModeratorRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/recommendations/:id"
                    element={
                      <ProtectedRoute>
                        <ModeratorRoute>
                          <GrantsRecommendationDetails />
                        </ModeratorRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/alerts"
                    element={
                      <ProtectedRoute>
                        <GrantsAlerts />
                      </ProtectedRoute>
                    }
                  />
                  {/* Client-facing article-centric list (new, grants:calls guard) */}
                  <Route
                    path="/grants/articles"
                    element={
                      <ProtectedRoute>
                        <GrantsArticlesTab />
                      </ProtectedRoute>
                    }
                  />
                  {/* Legacy routes for backward compatibility */}
                  <Route
                    path="/grants/discovery-articles"
                    element={
                      <ProtectedRoute>
                        <DiscoveryArticles />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/kfs"
                    element={
                      <ProtectedRoute>
                        <GrantsKfs />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tenant/users"
                    element={
                      <ProtectedRoute>
                        <TenantUsers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tenant/api-keys"
                    element={
                      <ProtectedRoute>
                        <ApiKeys />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tenant/service-accounts"
                    element={
                      <ProtectedRoute>
                        <ServiceAccounts />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/tenants"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminTenants />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/tenants/legacy"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <TenantManagement />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/tenants/:tenantId"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <TenantDetails />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/tenant-deletions"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminTenantDeletions />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/tenant-deletions/:archiveId"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminTenantDeletionDetails />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/subscription-plans"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <SubscriptionPlansManagement />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/brands"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <BrandManagement />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/kiedynabory-settlements"
                    element={
                      <ProtectedRoute>
                        <KiedyNaborySettlements />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/proforma-invoices"
                    element={
                      <ProtectedRoute>
                        <AdminProformaInvoices />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/admin/llm-usage"
                    element={
                      <ProtectedRoute>
                        <ModeratorRoute>
                          <LlmUsage />
                        </ModeratorRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/admin/form-generations"
                    element={
                      <ProtectedRoute>
                        <ModeratorRoute>
                          <FormGenerations />
                        </ModeratorRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/admin/email-tracking"
                    element={
                      <ProtectedRoute>
                        <ModeratorRoute>
                          <EmailTracking />
                        </ModeratorRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grants/admin/call-views"
                    element={
                      <ProtectedRoute>
                        <ModeratorRoute>
                          <CallViews />
                        </ModeratorRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/outbox"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <OutboxDashboard />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/grants/watermark-recovery"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <WatermarkRecovery />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  {/* Billing Admin (PA-2) */}
                  <Route
                    path="/admin/billing/products"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <BillingProducts />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/billing/catalog"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <BillingCatalog />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/trial-invites"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminTrialInvites />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/billing/subscriptions"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <BillingSubscriptions />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/billing/orders"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <BillingOrders />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/billing/pricing"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <BillingPricing />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  {/* AC-B4: Unmatched payments page */}
                  <Route
                    path="/admin/billing/payments"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <UnmatchedPaymentsPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/help"
                    element={
                      <ProtectedRoute>
                        <Help />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
                <ToastContainer />
                <CookieBanner />
              </div>
              </Router>
            </LegalConsentProvider>
            </ErrorBoundary>
          </ToastProvider>
        </HotelProvider>
      </TurnstileProvider>
    </QueryClientProvider>
  );
}

export default App;
